// Notes Storage Backend
// Provides storage for user notes using the store abstraction layer
//
// IMPORTANT: This module does NOT directly depend on platform.
// Storage implementation must be injected from upstream.

const NAMESPACE = 'notes'
const COLLECTION = 'notes'

/**
 * Notes Storage Backend
 * Wraps any store implementation to provide notes-specific operations
 *
 * @param {Object} store - Storage implementation (must implement Store interface)
 */
export class NotesStorage {
  constructor(store) {
    if (!store) {
      throw new Error('Storage implementation is required. Pass a store instance to the constructor.')
    }
    this.store = store
  }

  /**
   * Load all notes for a user
   * @param {string} userId - User ID
   * @returns {Promise<Array>} Array of notes
   */
  async loadNotes(userId) {
    const data = await this.store.get(NAMESPACE, userId, COLLECTION)
    if (!data) {
      return []
    }
    return data.notes || []
  }

  /**
   * Save all notes for a user
   * @param {string} userId - User ID
   * @param {Array} notes - Array of notes
   */
  async saveNotes(userId, notes) {
    const data = {
      version: '1.0',
      userId,
      notes,
      metadata: {
        totalNotes: notes.length,
        lastUpdated: new Date().toISOString(),
      },
    }

    await this.store.set(NAMESPACE, userId, COLLECTION, data)
  }

  /**
   * Add a new note
   * @param {string} userId - User ID
   * @param {Object} note - Note object
   * @returns {Promise<Object>} The added note
   */
  async addNote(userId, note) {
    const notes = await this.loadNotes(userId)
    notes.push(note)
    await this.saveNotes(userId, notes)
    return note
  }

  /**
   * Get a note by ID
   * @param {string} userId - User ID
   * @param {string} noteId - Note ID
   * @returns {Promise<Object|null>} The note or null if not found
   */
  async getNote(userId, noteId) {
    const notes = await this.loadNotes(userId)
    return notes.find((n) => n.id === noteId) || null
  }

  /**
   * Update a note
   * @param {string} userId - User ID
   * @param {string} noteId - Note ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated note
   */
  async updateNote(userId, noteId, updates) {
    const notes = await this.loadNotes(userId)
    const index = notes.findIndex((n) => n.id === noteId)

    if (index === -1) {
      throw new Error('Note not found')
    }

    notes[index] = {
      ...notes[index],
      ...updates,
      id: notes[index].id, // Preserve ID
      userId: notes[index].userId, // Preserve userId
      createdAt: notes[index].createdAt, // Preserve createdAt
      updatedAt: new Date().toISOString(),
    }

    await this.saveNotes(userId, notes)
    return notes[index]
  }

  /**
   * Delete a note
   * @param {string} userId - User ID
   * @param {string} noteId - Note ID
   * @returns {Promise<boolean>} True if deleted, false if not found
   */
  async deleteNote(userId, noteId) {
    const notes = await this.loadNotes(userId)
    const filtered = notes.filter((n) => n.id !== noteId)

    if (filtered.length === notes.length) {
      return false // Nothing was deleted
    }

    await this.saveNotes(userId, filtered)
    return true
  }

  /**
   * Get statistics for a user's notes
   * @param {string} userId - User ID
   * @returns {Promise<Object>} Statistics
   */
  async getStats(userId) {
    const notes = await this.loadNotes(userId)

    if (notes.length === 0) {
      return {
        totalNotes: 0,
        lastUpdated: null,
      }
    }

    // Find the most recently updated note
    const lastUpdated = notes.reduce((latest, note) => {
      const noteDate = new Date(note.updatedAt)
      return noteDate > latest ? noteDate : latest
    }, new Date(0))

    return {
      totalNotes: notes.length,
      lastUpdated: lastUpdated.toISOString(),
    }
  }
}

export default NotesStorage
